import React, { useState, useEffect } from 'react';
import { GraduationCap, Search, ChevronDown, ChevronUp, Check, FileText, Award, Plus, Users, MessageSquare, Settings } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface CourseBuilderContentProps {
  animationDelay?: number;
}

const sidebarItems = [
  { icon: FileText, label: 'Outline', active: true },
  { icon: Settings, label: 'Details', active: false },
  { icon: Settings, label: 'Customize', active: false },
  { icon: Award, label: 'Offers', active: false },
  { icon: MessageSquare, label: 'Comments', active: false },
  { icon: Award, label: 'Credentials', active: false },
  { icon: Users, label: 'Community Groups', active: false },
];

const lessons = [
  { title: 'Welcome Aboard!', status: 'Published' },
  { title: 'Lesson 1: The What', status: 'Published' },
  { title: 'Lesson 2: The Why', status: 'Published' },
  { title: 'Lesson 3: The How', status: 'Published' },
  { title: "What's Next?", status: 'Published' },
];

const CourseBuilderContent: React.FC<CourseBuilderContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [showLessons, setShowLessons] = useState<number[]>([]);
  const [expandedSection, setExpandedSection] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [showCredential, setShowCredential] = useState(false);

  useEffect(() => {
    // Animate lessons appearing one by one
    const timer1 = setTimeout(() => setShowLessons([0]), animationDelay + 600);
    const timer2 = setTimeout(() => setShowLessons([0, 1]), animationDelay + 800);
    const timer3 = setTimeout(() => setShowLessons([0, 1, 2]), animationDelay + 1000);
    const timer4 = setTimeout(() => setShowLessons([0, 1, 2, 3]), animationDelay + 1200);
    const timer5 = setTimeout(() => setShowLessons([0, 1, 2, 3, 4]), animationDelay + 1400);
    
    // Expand section
    const timer6 = setTimeout(() => setExpandedSection(true), animationDelay + 500);
    
    // Show badge and credential
    const timer7 = setTimeout(() => setShowBadge(true), animationDelay + 400);
    const timer8 = setTimeout(() => setShowCredential(true), animationDelay + 1600);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      clearTimeout(timer5);
      clearTimeout(timer6);
      clearTimeout(timer7);
      clearTimeout(timer8);
    };
  }, [animationDelay]);

  return (
    <div 
      className="flex-1 flex overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#222222' : '#F9F9F9',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Course Sidebar */}
      <div 
        className="w-32 border-r flex-shrink-0 py-3 px-2"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF'
        }}
      >
        {sidebarItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <div
              key={index}
              className="flex items-center gap-2 px-2 py-1.5 rounded mb-0.5 cursor-pointer"
              style={{ 
                backgroundColor: item.active 
                  ? (isDark ? '#333333' : '#F0F0F0')
                  : 'transparent',
                animation: `slideInFromLeft 0.3s ease-out ${animationDelay + 100 + (index * 50)}ms both`
              }}
            >
              <Icon 
                className="w-3 h-3" 
                style={{ color: item.active ? '#3B82F6' : (isDark ? '#888888' : '#666666') }} 
              />
              <span 
                className="text-[9px] font-medium"
                style={{ color: item.active ? '#3B82F6' : (isDark ? '#888888' : '#666666') }}
              >
                {item.label}
              </span>
            </div>
          );
        })}
      </div>

        {/* Main Content */}
        <div className="flex-1 p-4 overflow-hidden sm:overflow-auto max-h-[140px] sm:max-h-none">
        {/* Header */}
        <div 
          className="flex items-center justify-between mb-4"
          style={{
            animation: `fadeIn 0.4s ease-out ${animationDelay + 200}ms both`
          }}
        >
          {/* Search */}
          <div 
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg flex-1 max-w-xs"
            style={{ 
              backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
              border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`
            }}
          >
            <Search className="w-3 h-3" style={{ color: isDark ? '#666666' : '#999999' }} />
            <span 
              className="text-[9px]"
              style={{ color: isDark ? '#666666' : '#999999' }}
            >
              Search Module or Lesson
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button 
              className="px-2 py-1 rounded text-[9px] font-medium"
              style={{ 
                border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
                color: isDark ? '#FFFFFF' : '#111111',
                backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF'
              }}
            >
              Preview
            </button>
            <button 
              className="px-2 py-1 rounded text-[9px] font-medium text-white flex items-center gap-1"
              style={{ 
                backgroundColor: '#3B82F6',
                animation: `pulseGlow 2s ease-in-out infinite`
              }}
            >
              <Plus className="w-2.5 h-2.5" />
              Add Content
            </button>
          </div>
        </div>

        {/* Module Count */}
        <div 
          className="flex items-center justify-between mb-3"
          style={{
            animation: `fadeIn 0.4s ease-out ${animationDelay + 300}ms both`
          }}
        >
          <span 
            className="text-[10px] font-semibold"
            style={{ color: isDark ? '#FFFFFF' : '#111111' }}
          >
            1 Module
          </span>
          <button 
            className="text-[9px] flex items-center gap-1"
            style={{ color: isDark ? '#888888' : '#666666' }}
          >
            <ChevronUp className="w-3 h-3" />
            Collapse All
          </button>
        </div>

        {/* Welcome Badge */}
        <div 
          className="flex items-center justify-between py-2 px-3 mb-2 rounded-lg"
          style={{ 
            backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            opacity: showBadge ? 1 : 0,
            transform: showBadge ? 'translateX(0)' : 'translateX(-10px)',
            transition: 'all 0.4s ease-out'
          }}
        >
          <div className="flex items-center gap-2">
            <Award className="w-3.5 h-3.5" style={{ color: '#F59E0B' }} />
            <span 
              className="text-[10px] font-medium"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              Welcome Badge
            </span>
          </div>
          <span 
            className="text-[9px] font-medium flex items-center gap-1"
            style={{ color: '#10B981' }}
          >
            <Check className="w-3 h-3" />
            Published
          </span>
        </div>

        {/* Course Contents Section */}
        <div 
          className="rounded-lg overflow-hidden mb-2"
          style={{ 
            backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            animation: `fadeIn 0.4s ease-out ${animationDelay + 400}ms both`
          }}
        >
          {/* Section Header */}
          <div 
            className="flex items-center justify-between py-2 px-3 border-b cursor-pointer"
            style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" style={{ color: isDark ? '#888888' : '#666666' }} />
              <span 
                className="text-[10px] font-medium"
                style={{ color: isDark ? '#FFFFFF' : '#111111' }}
              >
                Course Contents
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                className="text-[9px] font-medium"
                style={{ color: '#3B82F6' }}
              >
                + Add Content
              </button>
              <span 
                className="text-[9px] font-medium flex items-center gap-1"
                style={{ color: '#10B981' }}
              >
                <Check className="w-3 h-3" />
                Published
              </span>
              {expandedSection ? (
                <ChevronUp className="w-3.5 h-3.5" style={{ color: isDark ? '#888888' : '#666666' }} />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" style={{ color: isDark ? '#888888' : '#666666' }} />
              )}
            </div>
          </div>

          {/* Lessons */}
          {expandedSection && (
            <div className="py-1">
              {lessons.map((lesson, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between py-2 px-4 hover:bg-opacity-50"
                  style={{ 
                    backgroundColor: 'transparent',
                    opacity: showLessons.includes(index) ? 1 : 0,
                    transform: showLessons.includes(index) ? 'translateX(0)' : 'translateX(-20px)',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-3 h-3" style={{ color: isDark ? '#666666' : '#888888' }} />
                    <span 
                      className="text-[9px]"
                      style={{ color: isDark ? '#CCCCCC' : '#333333' }}
                    >
                      {lesson.title}
                    </span>
                  </div>
                  <span 
                    className="text-[8px] font-medium flex items-center gap-0.5"
                    style={{ color: '#10B981' }}
                  >
                    <Check className="w-2.5 h-2.5" />
                    {lesson.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Course Completion Credential */}
        <div 
          className="flex items-center justify-between py-2 px-3 rounded-lg"
          style={{ 
            backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            opacity: showCredential ? 1 : 0,
            transform: showCredential ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.95)',
            transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        >
          <div className="flex items-center gap-2">
            <GraduationCap 
              className="w-3.5 h-3.5" 
              style={{ 
                color: '#8B5CF6',
                animation: showCredential ? 'bounceIn 0.5s ease-out' : 'none'
              }} 
            />
            <span 
              className="text-[10px] font-medium"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              Course Completion Credential
            </span>
            <span 
              className="w-3 h-3 rounded-full flex items-center justify-center text-[8px]"
              style={{ 
                backgroundColor: isDark ? '#333333' : '#E5E5E5',
                color: isDark ? '#888888' : '#666666'
              }}
            >
              i
            </span>
          </div>
          <span 
            className="text-[9px] font-medium flex items-center gap-1"
            style={{ color: '#10B981' }}
          >
            <Check className="w-3 h-3" />
            Published
          </span>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInFromLeft {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 15px rgba(59, 130, 246, 0.5); }
        }
        @keyframes bounceIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default CourseBuilderContent;
